/**
 * UserFilterPopover Component
 * 
 * User filter button and popover with user selection.
 * Uses FloatingLabelFilterButton for desktop, Select + Popover for mobile (matches Activity page).
 */

import React, { useEffect } from "react";
import { Grid, useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem, Popover, Box, FormGroup, FormControlLabel, Checkbox, Typography, Divider } from "@mui/material";
import { WorkspaceMember } from "../hooks/useAnalyticsWorkspaceMembers";
import FloatingLabelFilterButton, { FilterOption } from "../../../components/FloatingLabelFilterButton";

export interface UserFilterPopoverProps {
  workspaceMembers: WorkspaceMember[];
  selectedUsers: string[];
  onUserToggle: (userId: string) => void;
  onSelectAllUsers: () => void;
  onFilterClick: (event: React.MouseEvent<HTMLElement>) => void;
  onFilterClose: () => void;
  anchorEl: HTMLElement | null;
  getUserName: (userId: string) => string;
  transactions?: Array<{ userId?: string }>; // Optional: to check which users have created transactions
}

/**
 * User filter button and popover
 * Matches Activity page behavior exactly: mobile uses Select + Popover, desktop uses FloatingLabelFilterButton
 */
export const UserFilterPopover: React.FC<UserFilterPopoverProps> = ({
  workspaceMembers,
  selectedUsers,
  onUserToggle,
  onSelectAllUsers,
  onFilterClick,
  onFilterClose,
  anchorEl,
  getUserName,
  transactions = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (workspaceMembers.length === 0) {
    return null;
  }

  // Filter out orphaned user IDs and fake users (same simple logic as account filter)
  const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
  const isFakeUserId = (userId: string): boolean => {
    return ["demo-user"].includes(userId);
  };
  
  // Show all workspace members (filter out orphaned, fake users, and users without name/email)
  // Users without name/email are orphaned members and should not appear in the filter
  const filteredMembers = workspaceMembers.filter(user => {
    // Filter out orphaned IDs and fake users
    if (isOrphanedUserId(user.id) || isFakeUserId(user.id)) {
      return false;
    }
    // Filter out users without name or email (orphaned members)
    // Exception: bypass-user is always included (special system user)
    if (user.id === "bypass-user") {
      return true;
    }
    // User must have either name or email to be valid
    return !!(user.name || user.email);
  });
  
  // Disable entire filter only if there are no users
  const hasOnlyOneUser = filteredMembers.length === 0;
  
  const filteredSelectedUsers = selectedUsers.filter(id => 
    !isOrphanedUserId(id) && !isFakeUserId(id)
  );

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (hasOnlyOneUser) {
      return;
    }
    onFilterClick(event);
  };
  
  // Convert workspace members to FilterOption format (simple - no disabling, like account filter)
  const userOptions: FilterOption[] = filteredMembers.map(user => ({
    id: user.id,
    label: getUserName(user.id),
  }));

  // Handle selection change - same simple logic as account filter
  const handleSelectionChange = (selectedIds: string[]): void => {
    // Filter out orphaned user IDs and fake users from incoming selection
    const cleanSelectedIds = selectedIds.filter(id => 
      !isOrphanedUserId(id) && !isFakeUserId(id)
    );
    
    // If selection matches "all" or "none", use the select all handler
    if (cleanSelectedIds.length === 0 || cleanSelectedIds.length === filteredMembers.length) {
      onSelectAllUsers();
      return;
    }
    
    // Otherwise, set the selection directly
    // We need to update each user individually through onUserToggle
    const currentSet = new Set(filteredSelectedUsers);
    const newSet = new Set(cleanSelectedIds);
    
    // Add missing users
    cleanSelectedIds.forEach(id => {
      if (!currentSet.has(id)) {
        onUserToggle(id);
      }
    });
    
    // Remove users that shouldn't be selected
    filteredSelectedUsers.forEach(id => {
      if (!newSet.has(id)) {
        onUserToggle(id);
      }
    });
  };

  // Mobile implementation - matches Activity FilterButtons exactly
  if (isMobile) {
    return (
      <>
        {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>User</InputLabel>
            <Select
              value={filteredMembers.length === 0 
                ? "All"
                : filteredSelectedUsers.length === 0 || filteredSelectedUsers.length === filteredMembers.length
                ? "All"
                : filteredSelectedUsers.length === 1
                ? filteredSelectedUsers[0]
                : "Multiple"}
              label="User"
              open={false}
              readOnly
              onClick={handleFilterClick}
              sx={{ 
                cursor: hasOnlyOneUser ? "default" : "pointer",
                pointerEvents: hasOnlyOneUser ? "none" : "auto",
                "& .MuiSelect-select": {
                  pointerEvents: hasOnlyOneUser ? "none" : "auto",
                },
              }}
              disabled={hasOnlyOneUser}
              renderValue={(value) => {
                if (value === "All") return "All Users";
                if (value === "Multiple") return `${filteredSelectedUsers.length} Users`;
                return getUserName(value as string);
              }}
            >
              <MenuItem value="All">All Users</MenuItem>
            </Select>
          </FormControl>
          {filteredMembers.length > 0 && !hasOnlyOneUser && (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
              <Box sx={{ p: 1, minWidth: 200, maxWidth: 280, maxHeight: 350, overflowY: "auto", overflowX: "hidden" }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          filteredSelectedUsers.length === 0 || filteredSelectedUsers.length === filteredMembers.length
                        }
                        indeterminate={
                          filteredSelectedUsers.length > 0 &&
                          filteredSelectedUsers.length < filteredMembers.length
                        }
                        onChange={onSelectAllUsers}
                      />
                    }
                    label={<Typography variant="body2">All Users</Typography>}
                    sx={{ mb: 0.5 }}
                  />
                  <Divider sx={{ my: 0.5 }} />
                  {filteredMembers.map((user) => {
                    const displayLabel = getUserName(user.id);
                    
                    return (
                      <FormControlLabel
                        key={user.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={filteredSelectedUsers.includes(user.id)}
                            onChange={() => onUserToggle(user.id)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {displayLabel}
                          </Typography>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </Box>
            </Popover>
          )}
        </Grid>
      </>
    );
  }

  // Desktop implementation - uses FloatingLabelFilterButton
  return (
    <>
      {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
      <Grid item xs={12} sm={6} md={4}>
        <FloatingLabelFilterButton
          label="User"
          options={userOptions}
          selectedIds={filteredSelectedUsers}
          onSelectionChange={handleSelectionChange}
          getDisplayName={getUserName}
          allSelectedText="All Users"
          multipleSelectedText={(count: number) => `${count} Users`}
          anchorEl={anchorEl}
          onOpen={handleFilterClick}
          onClose={onFilterClose}
          groupOptions={false}
          minWidth={130}
          disabled={hasOnlyOneUser} // Disable only if no users
        />
      </Grid>
    </>
  );
};
